import {
  faComments,
  faEdit,
  faLinkSlash,
  faMinus,
  faPlus,
  faShareNodes,
  faSpinner,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CloseButton } from "../components/CloseButton";
import { SideBar } from "../components/SideBar";
import { useAccount, useToast, useWorkspaces } from "../state";
import {
  filterByKind,
  KIND_BADGE,
  KIND_FILTERS,
  KIND_LABEL,
  KindFilter,
  WorkspaceKind,
} from "./kind";
import { KindBadge } from "./KindBadge";
import { explorationName } from "./names";
import { WebDavLink } from "./WebDavLink";

// De + maakt een project of een verkenning. Een verkenning mag zonder naam
// beginnen en opent meteen: zoeken doe je daarin, met de facetten of de chat.
// Zonder de workspace-feature bestaat alleen het project.
export const NewWorkspace = () => {
  const { createWorkspace } = useWorkspaces();
  const chatEnabled = useAccount((s) => s.user?.features?.workspace);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<WorkspaceKind>("project");
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const exploration = kind === "exploration";

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newWorkspaceName.trim();
    if (!exploration) {
      if (!name) return;
      await createWorkspace(name);
      setNewWorkspaceName("");
      return;
    }
    const created = await createWorkspace(name || explorationName({}, new Date()), {
      criteria: {},
    });
    if (created) navigate(`/workspace/${created.id}`);
  };
  const btn = (
    <button
      onClick={() => setOpen(!open)}
      className={
        "px-1 py-1 float-right mr-4 rounded text-white " +
        (open ? "bg-red-500 hover:bg-red-700" : "bg-green hover:bg-green-800")
      }
      style={{ marginTop: "-50px" }}
      title={open ? "Sluiten" : "Nieuwe werkruimte"}
      aria-label={open ? "Sluiten" : "Nieuwe werkruimte"}
    >
      <FontAwesomeIcon icon={open ? faMinus : faPlus} />
    </button>
  );

  if (!open) return btn;
  return (
    <>
      {btn}
      <form onSubmit={handleCreate} className="mb-6 flex flex-col gap-2">
        {chatEnabled && (
          <div className="inline-flex self-start rounded-md border text-xs" role="group">
            {(["project", "exploration"] as WorkspaceKind[]).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setKind(option)}
                aria-pressed={kind === option}
                className={`px-2 py-1 first:rounded-s-md last:rounded-e-md ${
                  kind === option ? KIND_BADGE[option] : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {KIND_LABEL[option]}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={newWorkspaceName}
            onChange={(e) => setNewWorkspaceName(e.target.value)}
            placeholder={exploration ? "Naam (mag leeg blijven)" : "Nieuw project naam"}
            className="flex-1 p-2 border rounded text-black"
          />
          <button
            type="submit"
            disabled={!exploration && !newWorkspaceName.trim()}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {exploration ? "Begin" : "Toevoegen"}
          </button>
        </div>
        {exploration && (
          <p className="text-xs text-gray-500">
            Je zoekt daarna in de verkenning zelf, met de filters of de chat. Vanuit Zoek kan het
            ook, met &lsquo;Zoeken met chat&rsquo;.
          </p>
        )}
      </form>
    </>
  );
};

// Deel-link beheren: aanmaken kopieert de publieke /shared/<token>-URL,
// intrekken maakt de link ongeldig (opnieuw delen geeft een nieuwe token).
const ShareButton = ({ workspace }) => {
  const fetchWorkspaces = useWorkspaces((s) => s.fetchWorkspaces);
  const show = useToast((s) => s.show);

  const copy = async (token: string) => {
    const url = `${window.location.origin}/shared/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      show("Deel-link gekopieerd naar het klembord");
    } catch {
      // Safari kan kopiëren na een fetch weigeren (user-gesture verlopen):
      // toon de link dan selecteerbaar in de melding
      show(`Kopieer de deel-link: ${url}`, 15000);
    }
  };
  const share = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const r = await fetch(`/api/workspaces/${workspace.id}/share`, { method: "POST" });
    const { token } = await r.json();
    await copy(token);
    fetchWorkspaces(true);
  };
  const unshare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await fetch(`/api/workspaces/${workspace.id}/share`, { method: "DELETE" });
    show("Delen gestopt — de link is niet meer geldig");
    fetchWorkspaces(true);
  };

  return (
    <>
      <button
        onClick={share}
        className={
          workspace.shareToken
            ? "text-green hover:text-dark-green"
            : "text-gray-400 hover:text-green"
        }
        title={workspace.shareToken ? "Gedeeld — kopieer link" : "Deel via publieke link"}
      >
        <FontAwesomeIcon icon={faShareNodes} />
      </button>
      {workspace.shareToken && (
        <button
          onClick={unshare}
          className="text-gray-400 hover:text-red-600"
          title="Stop met delen (link vervalt)"
        >
          <FontAwesomeIcon icon={faLinkSlash} />
        </button>
      )}
    </>
  );
};

const WorkspaceItem = ({ workspace, edit }) => {
  const { deleteWorkspace, selectWorkspace } = useWorkspaces();
  const chatEnabled = useAccount((s) => s.user?.features?.workspace);
  const navigate = useNavigate();

  const handleSelect = useCallback(
    (e: React.MouseEvent) => {
      // Prevent selection when clicking edit or delete buttons
      if ((e.target as HTMLElement).closest("button")) return;

      selectWorkspace(workspace.id);
      navigate(`/workspaces/${workspace.id}`);
    },
    [workspace.id, selectWorkspace, navigate]
  );

  return (
    <div onClick={handleSelect} className="cursor-pointer">
      <div className="flex justify-between items-start">
        <h2 className="text-black">
          <button
            onClick={() => selectWorkspace(workspace.id)}
            className="text-black hover:text-blue-500"
          >
            {workspace.name}
          </button>
        </h2>
        <div className="flex gap-2">
          {chatEnabled && (
            <button
              onClick={() => navigate(`/workspace/${workspace.id}`)}
              className="text-gray-400 hover:text-black"
              title="Open workspace"
              aria-label="Open workspace"
            >
              <FontAwesomeIcon icon={faComments} />
            </button>
          )}
          <ShareButton workspace={workspace} />
          <button
            onClick={(e) => {
              e.stopPropagation();
              edit(workspace);
            }}
            className="text-blue-500 hover:text-blue-700"
            title="Bewerken"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm("Weet je het zeker?")) deleteWorkspace(workspace.id);
            }}
            className="text-gray-500 hover:text-red-500"
            title="Verwijderen"
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        <KindBadge workspace={workspace} className="mr-1" />
        {workspace.origin && <>uit verkenning {workspace.origin.name}</>}
      </p>
      <p className="text-gray-600">
        <a href={workspace.link || ""} onClick={(e) => e.stopPropagation()}>
          {workspace.description}
        </a>
      </p>
    </div>
  );
};

const KindToggle = ({
  value,
  onChange,
}: {
  value: KindFilter;
  onChange: (value: KindFilter) => void;
}) => (
  <div className="mb-3 inline-flex rounded-md border text-xs" role="group" aria-label="Soort">
    {KIND_FILTERS.map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => onChange(option.value)}
        aria-pressed={value === option.value}
        className={`px-2 py-1 first:rounded-s-md last:rounded-e-md ${
          value === option.value ? "bg-green text-white" : "bg-white text-gray-600 hover:bg-gray-50"
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

export const Workspaces = () => {
  const { workspaces, fetchWorkspaces, updateWorkspace, selectWorkspace, state } = useWorkspaces();
  const [editingWorkspaceId, setEditingWorkspaceId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const { id } = useParams();
  useEffect(() => selectWorkspace((id && parseInt(id)) || null), [id, selectWorkspace]);

  if (!workspaces) {
    return (
      <SideBar>
        <div className="p-6">
          <div>
            <FontAwesomeIcon icon={faSpinner} spin={true} fill="green" />
            {state === "error" ? (
              <>
                Fout...{" "}
                <button
                  className="text-white bg-green hover:bg-green-800 rounded px-2 py-1"
                  onClick={() => fetchWorkspaces(true)}
                >
                  opnieuw
                </button>
              </>
            ) : (
              "Laden..."
            )}
          </div>
        </div>
      </SideBar>
    );
  }

  const selectedWorkspaceId = id ? parseInt(id) : null;

  const handleUpdate = async (e: React.FormEvent, id: number) => {
    e.preventDefault();
    if (editingName.trim()) {
      await updateWorkspace(id, editingName);
      setEditingWorkspaceId(null);
    }
  };

  const startEditing = (workspace: any) => {
    setEditingWorkspaceId(workspace.id);
    setEditingName(workspace.name);
  };

  return (
    <SideBar>
      <div className="p-4 pb-14">
        <CloseButton className="text-black hover:text-gray hover:bg-black" />
        <h1 className="text-xl font-semibold mb-3">Werkruimtes</h1>
        <WebDavLink />
        <NewWorkspace />
        <KindToggle value={kindFilter} onChange={setKindFilter} />
        <ul>
          {filterByKind(workspaces, kindFilter).map((workspace) => (
            <li
              key={workspace.id}
              className={`p-2 border-gray-100 border-b first:rounded-t last:rounded-b shadow ${
                selectedWorkspaceId === workspace.id ? "bg-sidebar-menu-icon" : "bg-white"
              }`}
            >
              {editingWorkspaceId === workspace.id ? (
                <form
                  onSubmit={(e) => handleUpdate(e, workspace.id)}
                  className="flex flex-col gap-2"
                >
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="p-2 border rounded text-black"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="bg-green-500 text-white px-3 py-1 rounded text-sm"
                    >
                      Opslaan
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingWorkspaceId(null)}
                      className="bg-gray-500 text-white px-3 py-1 rounded text-sm"
                    >
                      Annuleren
                    </button>
                  </div>
                </form>
              ) : (
                <WorkspaceItem workspace={workspace} edit={startEditing} />
              )}
            </li>
          ))}
        </ul>
      </div>
    </SideBar>
  );
};
